import { type ReactNode, useEffect, useState } from 'react'
import {
  type DateRange,
  DayPicker,
  type PropsMulti as DayPickerMultipleProps,
  type PropsRange as DayPickerRangeProps,
  type PropsSingle as DayPickerSingleProps,
  type OnSelectHandler,
  type PropsBase,
} from 'react-day-picker'

import { FvButton } from '@fv/client-components'
import { fvDateUtc } from '@fv/dates'

import { Pinline } from '@/components/Pinline'
import { ShipperSlider } from '@/components/ShipperSlider'

import { hasCompleteDateRange } from './dateRange'

type ExtendedProps = {
  open?: boolean
  onClose?: () => void
  rightContent?: ReactNode
  showClear?: boolean
  onClear?: () => void
  onCancel?: () => void
  saveText?: string
  isSaving?: boolean
}

type SinglePanelProps = DayPickerSingleProps & {
  onSave?: (val: Date | undefined) => void
  titleContent?: (val: Date | undefined) => ReactNode
}
type MultiplePanelProps = DayPickerMultipleProps & {
  onSave?: (val: Date[]) => void
  titleContent?: (val: Date[]) => ReactNode
}
type RangePanelProps = DayPickerRangeProps & {
  onSave?: (val: DateRange | undefined) => void
  titleContent?: (val: DateRange | undefined) => ReactNode
}

type Props = PropsBase &
  (SinglePanelProps | MultiplePanelProps | RangePanelProps) &
  ExtendedProps

function isSameCalendarDay(left?: Date, right?: Date) {
  return fvDateUtc(left).isSame(right, 'day')
}

export const DayPickerPanel = ({
  open,
  onClose,
  rightContent,
  onSave,
  titleContent,
  showClear,
  onClear,
  onCancel,
  isSaving,
  saveText = 'Apply and close',
  ...dayPickerProps
}: Props) => {
  const showActions = !!onSave || showClear || !!onCancel
  const [localVal, setLocalVal] = useState<unknown>(
    dayPickerProps.selected as any,
  )
  const width =
    dayPickerProps.mode === 'range' || (dayPickerProps.numberOfMonths ?? 1) > 1
      ? '40.75rem'
      : !rightContent
        ? '22rem'
        : undefined
  const isIncompleteRange =
    dayPickerProps.mode === 'range' &&
    !!(localVal as DateRange | undefined)?.from &&
    !hasCompleteDateRange(localVal as DateRange | undefined)

  const handleSelect: OnSelectHandler<unknown> = (
    nextVal,
    clickedDate,
    modifiers,
    e,
  ) => {
    if (dayPickerProps.mode === 'range') {
      const previousVal = localVal as DateRange | undefined
      const val = nextVal as DateRange | undefined

      if (
        val?.from &&
        !val.to &&
        isSameCalendarDay(previousVal?.from, clickedDate) &&
        isSameCalendarDay(val.from, clickedDate)
      ) {
        nextVal = {
          ...val,
          to: val.from,
        }
      }
    }

    setLocalVal(nextVal as any)
    dayPickerProps.onSelect?.(nextVal as any, clickedDate, modifiers, e)
  }

  const handleSaveClick = () => {
    onSave?.(localVal as any)
  }

  useEffect(() => {
    if (open) {
      setLocalVal(dayPickerProps.selected)
    }
  }, [open, setLocalVal, dayPickerProps.selected])

  return (
    <ShipperSlider isOpen={open} onClose={onClose} width={width}>
      {titleContent?.(localVal as any)}
      <div className="flex gap-8">
        <div>
          <DayPicker
            {...dayPickerProps}
            selected={localVal as any}
            onSelect={handleSelect}
          />
        </div>
        {!!rightContent && <div>{rightContent}</div>}
      </div>
      {showActions && (
        <div>
          <Pinline className="my-6" />
          <div className="flex items-center">
            {onSave && (
              <FvButton
                theme="primary"
                icon="check"
                onClick={handleSaveClick}
                loading={isSaving}
                disabled={isIncompleteRange}
              >
                {saveText}
              </FvButton>
            )}
            {showClear && (
              <FvButton
                icon="times"
                theme="default"
                onClick={() => {
                  const val: any =
                    dayPickerProps.mode === 'multiple' ? [] : undefined
                  if (onSave) {
                    setLocalVal(val)
                  } else {
                    dayPickerProps.onSelect(val, null, null, null)
                  }
                  onClear?.()
                }}
              >
                Clear date
              </FvButton>
            )}
            {onCancel && (
              <FvButton theme="plain" icon="times" onClick={onCancel}>
                Cancel
              </FvButton>
            )}
          </div>
        </div>
      )}
    </ShipperSlider>
  )
}
